// src/pages/DashboardPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, User, Calendar, MessageSquare, Globe,
  TrendingUp, Clock, Hash, RefreshCw, MessageCircle, Tag, MessagesSquare, MessageSquareText,
  ChevronLeft, ChevronRight, Search, X, AlertTriangle, Shield, Eye, ArrowLeft,
  Monitor, Smartphone, Tablet, MapPin, Filter,
  ClipboardCheck, Lock, Zap, Inbox, Download, FileText, Target, Building2,
  ThumbsUp, ThumbsDown, Receipt
} from 'lucide-react';
import { pageVariants, pageTransition } from '../utils/pageTransitions';
import AcopMonitor from './AcopMonitor';
import UtilityAllowance from './UtilityAllowance';
import OutreachTab from './OutreachTab';
import QAResultsPage from './QAResultsPage';
import UATrackerTab from './UATrackerTab';
import LihtcTab from './LihtcTab';
import BillingTab from './BillingTab';
import HousingNavigatorAnalytics from './HousingNavigatorAnalytics';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8787'
  : 'https://housing-external.onrender.com';

const COMPLIANCE_API_URL =
  process.env.REACT_APP_COMPLIANCE_API ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3200'
    : 'https://housing-compliance-service.onrender.com');

const ADMIN_API_KEY = process.env.REACT_APP_ADMIN_API_KEY || '';

const LANGUAGE_NAMES = {
  en: 'English', es: 'Spanish', pt: 'Portuguese', zh: 'Chinese',
  fr: 'French', ht: 'Haitian Creole', it: 'Italian', ru: 'Russian',
  vi: 'Vietnamese', el: 'Greek', ar: 'Arabic',
  sq: 'Albanian', bn: 'Bengali', de: 'German', hi: 'Hindi',
  ja: 'Japanese', ko: 'Korean', pl: 'Polish', tl: 'Tagalog', uk: 'Ukrainian',
};

const getLanguageName = (code) => LANGUAGE_NAMES[code] || code?.toUpperCase() || '?';

const groupConversationsByLocalDate = (timestamps) => {
  if (!timestamps || !timestamps.length) return [];
  const grouped = {};
  timestamps.forEach(({ timestamp, conversations }) => {
    const localDate = new Date(timestamp);
    const dateKey = localDate.toLocaleDateString('en-CA');
    if (!grouped[dateKey]) grouped[dateKey] = 0;
    grouped[dateKey] += conversations;
  });
  return Object.entries(grouped)
    .map(([date, conversations]) => ({ date, conversations }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

// ─── Admin API helper ──────────────────────────────────────
async function adminFetch(path, params = {}) {
  const url = new URL(`${API_URL}/admin/data${path}`);
  url.searchParams.set('key', ADMIN_API_KEY);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) url.searchParams.set(k, v);
  });
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`Admin API error: ${res.status}`);
  return res.json();
}

// ─── Compliance-service admin helper (widgetboss UA requests) ──
// Same key-in-query convention as adminFetch above; the compliance service
// accepts x-api-key or ?key= on its /admin routes.
const COMPLIANCE_ADMIN_KEY =
  process.env.REACT_APP_COMPLIANCE_ADMIN_KEY || process.env.REACT_APP_ADMIN_API_KEY || '';

async function complianceAdminFetch(path, options = {}) {
  const url = new URL(`${COMPLIANCE_API_URL}/api/utility-allowance${path}`, window.location.origin);
  if (COMPLIANCE_ADMIN_KEY) url.searchParams.set('key', COMPLIANCE_ADMIN_KEY);
  const res = await fetch(url.toString(), options);
  if (!res.ok) throw new Error(`Compliance admin API error: ${res.status}`);
  return res.json();
}

// ─── Conversations Tab (widgetboss only) ───────────────────
const ConversationsTab = () => {
  const [conversations, setConversations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryPeriod, setSummaryPeriod] = useState(30);
  const LIMIT = 25;

  // Filters
  const [filters, setFilters] = useState({
    org: '', topic: '', device: '', country: '',
    flagged: '', pii: '', from: '', to: '', search: '', rating: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const loadConversations = useCallback(async (pageNum = 0, filterOverrides = null) => {
    setLoading(true);
    setError('');
    try {
      const f = filterOverrides || appliedFilters;
      // Map frontend filter names to backend param names
      const params = { page: pageNum + 1, limit: LIMIT };
      if (f.org) params.org = f.org;
      if (f.topic) params.topic = f.topic;
      if (f.device) params.device = f.device;
      if (f.country) params.country = f.country;
      if (f.flagged === 'true') params.flaggedOnly = 'true';
      if (f.flagged === 'false') params.flaggedOnly = 'false';
      if (f.pii === 'true') params.piiOnly = 'true';
      if (f.pii === 'false') params.piiOnly = 'false';
      if (f.from) params.dateFrom = f.from;
      if (f.to) params.dateTo = f.to;
      if (f.rating) params.rating = f.rating;

      const data = await adminFetch('/conversations', params);
      setConversations(data.conversations || []);
      setTotalCount(data.pagination?.total || 0);
      setPage(pageNum);
    } catch (err) {
      console.error('❌ Load conversations error:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  const loadSummary = async (days) => {
    setSummaryLoading(true);
    try {
      const data = await adminFetch('/conversations/summary', { days: days || summaryPeriod });
      setSummary(data);
    } catch (err) {
      console.error('❌ Load summary error:', err);
    } finally {
      setSummaryLoading(false);
    }
  };

  // Housing Navigator's monthly conversation cap (AI Search Mode sleeps at the cap)
  const [hnUsage, setHnUsage] = useState(null);
  const loadHnUsage = async () => {
    try { setHnUsage(await adminFetch('/hn-usage')); }
    catch (err) { console.error('❌ Load Housing Navigator usage error:', err); }
  };

  useEffect(() => { loadConversations(0); loadSummary(30); loadHnUsage(); }, []);

  const applyFilters = () => {
    const clean = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) clean[k] = v; });
    setAppliedFilters(clean);
    loadConversations(0, clean);
  };

  const clearFilters = () => {
    setFilters({ org: '', topic: '', device: '', country: '', flagged: '', pii: '', from: '', to: '', search: '', rating: '' });
    setAppliedFilters({});
    loadConversations(0, {});
  };

  const openConversation = async (sessionId) => {
    setSelectedSession(sessionId);
    setDetailLoading(true);
    try {
      const data = await adminFetch(`/conversations/${sessionId}`);
      setSessionDetail(data);
    } catch (err) {
      console.error('❌ Load conversation detail error:', err);
      setSessionDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedSession(null);
    setSessionDetail(null);
  };

  const totalPages = Math.ceil(totalCount / LIMIT);
  const activeFilterCount = Object.keys(appliedFilters).length;

  // ── Detail View ──
  if (selectedSession) {
    return (
      <div className="space-y-4">
        <button onClick={closeDetail} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium">
          <ArrowLeft className="h-4 w-4" /> Back to conversations
        </button>

        {detailLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : sessionDetail ? (
          <div className="space-y-4">
            {/* Session metadata */}
            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500 block">Session ID</span>
                <span className="font-medium font-mono text-xs select-all">{sessionDetail.session?.sessionId || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500 block">IP Address</span>
                <span className="font-medium font-mono text-xs select-all">{sessionDetail.session?.ip || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Organization</span>
                <span className="font-medium capitalize">{sessionDetail.session?.org || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Language</span>
                <span className="font-medium">{getLanguageName(sessionDetail.session?.language)}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Device</span>
                <span className="font-medium capitalize flex items-center gap-1">
                  {sessionDetail.session?.deviceType === 'mobile' && <Smartphone className="h-3 w-3" />}
                  {sessionDetail.session?.deviceType === 'desktop' && <Monitor className="h-3 w-3" />}
                  {sessionDetail.session?.deviceType === 'tablet' && <Tablet className="h-3 w-3" />}
                  {sessionDetail.session?.deviceType || '—'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Location</span>
                <span className="font-medium flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {[sessionDetail.session?.geo?.city, sessionDetail.session?.geo?.region, sessionDetail.session?.geo?.country].filter(Boolean).join(', ') || '—'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Started</span>
                <span className="font-medium">{sessionDetail.session?.startedAt ? new Date(sessionDetail.session.startedAt).toLocaleString() : '—'}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Messages</span>
                <span className="font-medium">{sessionDetail.session?.messageCount || 0}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Resolution</span>
                <span className={`font-medium capitalize ${sessionDetail.session?.resolutionStatus === 'resolved' ? 'text-green-600' : sessionDetail.session?.resolutionStatus === 'escalated' ? 'text-orange-600' : sessionDetail.session?.resolutionStatus === 'denied' ? 'text-red-600' : 'text-gray-600'}`}>
                  {sessionDetail.session?.resolutionStatus || 'unknown'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Visitor rating</span>
                <span className={`font-medium flex items-center gap-1 ${sessionDetail.session?.rating === 'up' ? 'text-green-600' : sessionDetail.session?.rating === 'down' ? 'text-red-600' : 'text-gray-600'}`}>
                  {sessionDetail.session?.rating === 'up' && <><ThumbsUp className="h-3.5 w-3.5" /> Good</>}
                  {sessionDetail.session?.rating === 'down' && <><ThumbsDown className="h-3.5 w-3.5" /> Bad</>}
                  {sessionDetail.session?.rating === 'skipped' && 'Skipped'}
                  {!sessionDetail.session?.rating && '—'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Repeat Visitor</span>
                <span className="font-medium">{sessionDetail.session?.isRepeatVisitor ? 'Yes' : 'No'}</span>
              </div>
            </div>

            {/* Messages */}
            <div className="space-y-3">
              {(sessionDetail.messages || []).map((msg, i) => (
                <div key={i} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-lg p-3 text-sm ${msg.type === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                    }`}>
                    <div className="whitespace-pre-wrap break-words">{msg.content || '[encrypted/unavailable]'}</div>
                    <div className={`text-xs mt-1 flex flex-wrap items-center gap-2 ${msg.type === 'user' ? 'text-blue-200' : 'text-gray-400'}`}>
                      <span>{msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}</span>
                      {msg.topicCategory && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">{msg.topicCategory}</span>}
                      {msg.flagged && <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-xs flex items-center gap-0.5"><AlertTriangle className="h-3 w-3" />{msg.flagReason}</span>}
                      {msg.piiDetected && <span className="bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded text-xs flex items-center gap-0.5"><Shield className="h-3 w-3" />PII: {(msg.piiTypes || []).join(', ')}</span>}
                      {msg.escalationTriggered && <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded text-xs">Escalated</span>}
                      {msg.confidenceScore != null && <span className="text-xs opacity-60">conf: {Number(msg.confidenceScore).toFixed(2)}</span>}
                      {msg.responseTimeMs != null && <span className="text-xs opacity-60">{msg.responseTimeMs}ms</span>}
                    </div>
                  </div>
                </div>
              ))}
              {(!sessionDetail.messages || sessionDetail.messages.length === 0) && (
                <p className="text-gray-500 text-center py-8">No messages found (this session may predate encryption logging)</p>
              )}
            </div>
          </div>
        ) : (
          <p className="text-red-500 text-center py-8">Failed to load conversation</p>
        )}
      </div>
    );
  }

  // ── List View ──
  return (
    <div className="space-y-4">

      {/* Housing Navigator: this month's conversations against the cap */}
      {hnUsage && hnUsage.cap > 0 && (() => {
        const pct = Math.min(100, Math.round((hnUsage.used / hnUsage.cap) * 100));
        const bar = hnUsage.used >= hnUsage.cap ? 'bg-red-600' : hnUsage.used >= hnUsage.alertAt ? 'bg-orange-500' : 'bg-blue-600';
        const resets = new Date(`${hnUsage.resetsOn}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return (
          <div className="bg-white rounded-lg border p-3 mb-3" title="Only conversations on housingnavigatorma.org pages count. Emails go to HNMI at 80% and at the cap.">
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="font-medium text-gray-900">Housing Navigator AI Search Mode · this month</span>
              <span className="text-gray-700"><span className="font-bold">{hnUsage.used}</span> of {hnUsage.cap} conversations{hnUsage.asleep ? ' · paused' : ''} · resets {resets}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={hnUsage.cap} aria-valuenow={hnUsage.used} aria-label="Housing Navigator conversations used this month">
              <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="text-xs text-gray-500 mt-1">Email to HNMI at {hnUsage.alertAt} and at {hnUsage.cap}{hnUsage.alertsConfigured ? '' : ' (alerts not configured on the server yet)'}</div>
          </div>
        );
      })()}

      {/* Summary Stats */}
      {summaryLoading && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      )}
      {!summaryLoading && summary && (
        <div className="space-y-4 mb-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Summary period:</span>
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => { setSummaryPeriod(d); loadSummary(d); }}
                className={`px-2 py-0.5 rounded text-xs ${summaryPeriod === d ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {d}d
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div className="bg-white rounded-lg border p-3 text-center">
              <div className="text-2xl font-bold text-gray-900">{summary.total}</div>
              <div className="text-xs text-gray-500">Total Sessions</div>
            </div>
            <div className="bg-white rounded-lg border p-3 text-center" title="Thumbs up / down from the visitor at the end of the chat (orgs with the prompt turned on)">
              <div className="text-2xl font-bold flex items-center justify-center gap-2">
                <span className="text-green-600 flex items-center gap-0.5"><ThumbsUp className="h-4 w-4" />{summary.ratings?.up ?? 0}</span>
                <span className="text-red-600 flex items-center gap-0.5"><ThumbsDown className="h-4 w-4" />{summary.ratings?.down ?? 0}</span>
              </div>
              <div className="text-xs text-gray-500">Visitor Rating{summary.ratings?.skipped ? ` · ${summary.ratings.skipped} skipped` : ''}</div>
            </div>
            <div className="bg-white rounded-lg border p-3 text-center">
              <div className="text-2xl font-bold text-orange-600">{summary.escalated}</div>
              <div className="text-xs text-gray-500">Escalated</div>
            </div>
            <div className="bg-white rounded-lg border p-3 text-center">
              <div className="text-2xl font-bold text-red-600">{summary.flagged}</div>
              <div className="text-xs text-gray-500">Flagged</div>
            </div>
            <div className="bg-white rounded-lg border p-3 text-center">
              <div className="text-2xl font-bold text-yellow-600">{summary.piiDetected}</div>
              <div className="text-xs text-gray-500">PII Detected</div>
            </div>
            <div className="bg-white rounded-lg border p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{summary.repeatVisitors}</div>
              <div className="text-xs text-gray-500">Repeat Visitors</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Topics</div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {(summary.topics || []).map(t => (
                  <div key={t.category} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 capitalize truncate">{t.category.replace('_', ' ')}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="w-16 bg-gray-100 rounded-full h-1.5">
                        <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, (t.count / (summary.topics[0]?.count || 1)) * 100)}%` }}></div>
                      </div>
                      <span className="text-xs text-gray-500 w-8 text-right">{t.count}</span>
                    </div>
                  </div>
                ))}
                {(!summary.topics || summary.topics.length === 0) && <span className="text-xs text-gray-400">No data yet</span>}
              </div>
            </div>

            <div className="bg-white rounded-lg border p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Resolution</div>
              <div className="space-y-1.5">
                {(summary.resolution || []).map(r => {
                  const colors = { resolved: 'bg-green-500', escalated: 'bg-orange-500', denied: 'bg-red-500', unresolved: 'bg-yellow-500', unknown: 'bg-gray-400' };
                  const textColors = { resolved: 'text-green-700', escalated: 'text-orange-700', denied: 'text-red-700', unresolved: 'text-yellow-700', unknown: 'text-gray-500' };
                  return (
                    <div key={r.status} className="flex items-center justify-between text-sm">
                      <span className={`capitalize ${textColors[r.status] || 'text-gray-700'}`}>{r.status}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-16 bg-gray-100 rounded-full h-1.5">
                          <div className={`${colors[r.status] || 'bg-gray-400'} h-1.5 rounded-full`} style={{ width: `${Math.min(100, (r.count / summary.total) * 100)}%` }}></div>
                        </div>
                        <span className="text-xs text-gray-500 w-8 text-right">{r.count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-lg border p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Devices</div>
              <div className="space-y-1.5">
                {(summary.devices || []).map(d => {
                  const icons = { mobile: '📱', desktop: '🖥️', tablet: '📟', unknown: '❓' };
                  return (
                    <div key={d.device} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 capitalize">{icons[d.device] || ''} {d.device}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-16 bg-gray-100 rounded-full h-1.5">
                          <div className="bg-purple-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, (d.count / summary.total) * 100)}%` }}></div>
                        </div>
                        <span className="text-xs text-gray-500 w-8 text-right">{d.count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-lg border p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Countries</div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {(summary.countries || []).map(c => (
                  <div key={c.country} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">{c.country}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="w-16 bg-gray-100 rounded-full h-1.5">
                        <div className="bg-cyan-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, (c.count / (summary.countries[0]?.count || 1)) * 100)}%` }}></div>
                      </div>
                      <span className="text-xs text-gray-500 w-8 text-right">{c.count}</span>
                    </div>
                  </div>
                ))}
                {(!summary.countries || summary.countries.length === 0) && <span className="text-xs text-gray-400">No data yet</span>}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white rounded-lg border p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Hours of Day (UTC)</div>
              {summary.hourly?.length > 0 ? (
                <>
                  <div className="flex items-end gap-px" style={{ height: '64px' }}>
                    {Array.from({ length: 24 }, (_, h) => {
                      const entry = summary.hourly.find(e => e.hour === h);
                      const count = entry?.count || 0;
                      const maxH = Math.max(...summary.hourly.map(e => e.count), 1);
                      return (
                        <div key={h} className="flex-1 group relative h-full flex flex-col justify-end">
                          <div className="bg-blue-400 hover:bg-blue-600 rounded-t transition-colors" style={{ height: `${Math.max((count / maxH) * 100, 2)}%` }}></div>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-1.5 py-0.5 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                            {h}:00 — {count}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span>
                  </div>
                </>
              ) : <span className="text-xs text-gray-400">No data yet</span>}
            </div>

            <div className="bg-white rounded-lg border p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Day of Week</div>
              {summary.daily?.length > 0 ? (
                <div className="space-y-1">
                  {[['Sunday', 0], ['Monday', 1], ['Tuesday', 2], ['Wednesday', 3], ['Thursday', 4], ['Friday', 5], ['Saturday', 6]].map(([dayName, dayNum]) => {
                    const entry = summary.daily.find(e => String(e.day) === String(dayNum));
                    const day = dayName;
                    const count = entry?.count || 0;
                    const maxD = Math.max(...summary.daily.map(e => e.count), 1);
                    return (
                      <div key={day} className="flex items-center gap-2 text-sm">
                        <span className="text-gray-600 w-8 text-xs">{day.slice(0, 3)}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full" style={{ width: `${Math.max((count / maxD) * 100, 1)}%` }}></div>
                        </div>
                        <span className="text-xs text-gray-500 w-6 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              ) : <span className="text-xs text-gray-400">No data yet</span>}
            </div>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-2 rounded border text-sm ${activeFilterCount > 0 ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
        >
          <Filter className="h-4 w-4" />
          Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
        </button>

        {activeFilterCount > 0 && (
          <button onClick={clearFilters} className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1">
            <X className="h-3 w-3" /> Clear
          </button>
        )}

        <span className="text-sm text-gray-500 ml-auto">{totalCount} conversation{totalCount !== 1 ? 's' : ''}</span>

        <button onClick={() => loadConversations(page)} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* Expandable filter panel */}
      {showFilters && (
        <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Organization</label>
            <input className="w-full border rounded px-2 py-1.5 text-sm" placeholder="e.g. salem"
              value={filters.org} onChange={e => setFilters(f => ({ ...f, org: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Topic</label>
            <select className="w-full border rounded px-2 py-1.5 text-sm bg-white"
              value={filters.topic} onChange={e => setFilters(f => ({ ...f, topic: e.target.value }))}>
              <option value="">All topics</option>
              {['rent', 'maintenance', 'eligibility', 'waitlist', 'section_8', 'contact', 'complaint', 'application', 'policy', 'board', 'staff', 'general'].map(t =>
                <option key={t} value={t}>{t.replace('_', ' ')}</option>
              )}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Device</label>
            <select className="w-full border rounded px-2 py-1.5 text-sm bg-white"
              value={filters.device} onChange={e => setFilters(f => ({ ...f, device: e.target.value }))}>
              <option value="">All devices</option>
              <option value="mobile">Mobile</option>
              <option value="desktop">Desktop</option>
              <option value="tablet">Tablet</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Country</label>
            <input className="w-full border rounded px-2 py-1.5 text-sm" placeholder="e.g. US"
              value={filters.country} onChange={e => setFilters(f => ({ ...f, country: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Flagged</label>
            <select className="w-full border rounded px-2 py-1.5 text-sm bg-white"
              value={filters.flagged} onChange={e => setFilters(f => ({ ...f, flagged: e.target.value }))}>
              <option value="">Any</option>
              <option value="true">Flagged only</option>
              <option value="false">Not flagged</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">PII Detected</label>
            <select className="w-full border rounded px-2 py-1.5 text-sm bg-white"
              value={filters.pii} onChange={e => setFilters(f => ({ ...f, pii: e.target.value }))}>
              <option value="">Any</option>
              <option value="true">Has PII</option>
              <option value="false">No PII</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Rating</label>
            <select className="w-full border rounded px-2 py-1.5 text-sm bg-white"
              value={filters.rating} onChange={e => setFilters(f => ({ ...f, rating: e.target.value }))}>
              <option value="">Any</option>
              <option value="up">Thumbs up</option>
              <option value="down">Thumbs down</option>
              <option value="skipped">Skipped</option>
              <option value="unrated">Not asked / no answer</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">From</label>
            <input type="date" className="w-full border rounded px-2 py-1.5 text-sm"
              value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">To</label>
            <input type="date" className="w-full border rounded px-2 py-1.5 text-sm"
              value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} />
          </div>
          <div className="md:col-span-4 flex justify-end">
            <button onClick={applyFilters} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      ) : conversations.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No conversations found</div>
      ) : (
        <>
          {/* Conversation list */}
          <div className="bg-white rounded-lg border divide-y">
            {conversations.map((conv) => (
              <div
                key={conv.sessionId}
                onClick={() => openConversation(conv.sessionId)}
                className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-medium text-gray-900 capitalize text-sm">{conv.org}</span>
                      <span className="text-xs text-gray-400 font-mono" title={conv.sessionId}>{conv.sessionId?.slice(0, 8)}</span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">{conv.startedAt ? new Date(conv.startedAt).toLocaleString() : '—'}</span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">{conv.messageCount || 0} msgs</span>
                      {conv.deviceType && (
                        <span className="text-xs text-gray-400 flex items-center gap-0.5">
                          {conv.deviceType === 'mobile' && <Smartphone className="h-3 w-3" />}
                          {conv.deviceType === 'desktop' && <Monitor className="h-3 w-3" />}
                          {conv.deviceType === 'tablet' && <Tablet className="h-3 w-3" />}
                        </span>
                      )}
                      {conv.geo?.country && <span className="text-xs text-gray-400">{conv.geo.city ? `${conv.geo.city}, ` : ''}{conv.geo.country}</span>}
                    </div>
                    {conv.firstMessage && (
                      <p className="text-sm text-gray-600 truncate">{conv.firstMessage}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {conv.rating === 'up' && <ThumbsUp className="h-4 w-4 text-green-600" title="Visitor: good experience" />}
                    {conv.rating === 'down' && <ThumbsDown className="h-4 w-4 text-red-600" title="Visitor: bad experience" />}
                    {conv.hasFlags && <AlertTriangle className="h-4 w-4 text-red-500" title="Flagged content" />}
                    {conv.hasPII && <Shield className="h-4 w-4 text-yellow-500" title="PII detected" />}
                    {conv.hasEscalation && <MessageCircle className="h-4 w-4 text-orange-500" title="Escalated" />}
                    <span className={`text-xs px-2 py-0.5 rounded capitalize ${conv.resolutionStatus === 'resolved' ? 'bg-green-100 text-green-700'
                      : conv.resolutionStatus === 'escalated' ? 'bg-orange-100 text-orange-700'
                        : conv.resolutionStatus === 'denied' ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                      {conv.resolutionStatus || 'unknown'}
                    </span>
                    <Eye className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <button
                onClick={() => loadConversations(page - 1)}
                disabled={page === 0}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded disabled:opacity-40 hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <span className="text-sm text-gray-600">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => loadConversations(page + 1)}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded disabled:opacity-40 hover:bg-gray-50"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Utility Allowance Requests Tab (widgetboss only) ──────
// Every UA join request submitted through the public website
// (gethousingai.com/utility-allowances/join), with pipeline status.
const UA_REQ_STATUSES = {
  new:       { label: 'New',       badge: 'bg-amber-100 text-amber-800' },
  active:    { label: 'Active',    badge: 'bg-blue-100 text-blue-800' },
  completed: { label: 'Completed', badge: 'bg-green-100 text-green-800' },
  declined:  { label: 'Declined',  badge: 'bg-gray-200 text-gray-600' },
};

// Funnel stage: how far the requester got. form_submitted = filled the form
// but took no payment action yet; contact_requested = chose "pay another
// way"; paid = completed Stripe checkout (set by webhook).
const UA_PAYMENT_STAGES = {
  form_submitted:    { label: 'Form only', badge: 'bg-amber-100 text-amber-800', hint: 'Submitted the form but has not paid or picked the contact option yet' },
  contact_requested: { label: 'Contact option', badge: 'bg-blue-100 text-blue-800', hint: 'Asked to pay another way (check, PO, ...)' },
  paid:              { label: 'Paid', badge: 'bg-green-100 text-green-800', hint: 'Completed Stripe checkout' },
};

const UARequestsTab = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());
  const [savingId, setSavingId] = useState(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await complianceAdminFetch('/admin/requests');
      setRequests(data.requests || []);
    } catch (err) {
      console.error('❌ Load UA requests error:', err);
      setError('Failed to load utility allowance requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const changeStatus = async (id, status) => {
    setSavingId(id);
    try {
      await complianceAdminFetch(`/admin/requests/${id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (err) {
      console.error('❌ Status update error:', err);
      alert('Failed to update status. Please try again.');
    } finally {
      setSavingId(null);
    }
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // fileId → one of the request's uploaded files; without it, the legacy
  // first-file endpoint.
  const fileUrl = (id, fileId) => {
    const path = fileId
      ? `/api/utility-allowance/admin/requests/${id}/files/${fileId}`
      : `/api/utility-allowance/admin/requests/${id}/schedule-file`;
    const url = new URL(`${COMPLIANCE_API_URL}${path}`, window.location.origin);
    if (COMPLIANCE_ADMIN_KEY) url.searchParams.set('key', COMPLIANCE_ADMIN_KEY);
    return url.toString();
  };

  const counts = requests.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});
  const shown = statusFilter === 'all' ? requests : requests.filter((r) => r.status === statusFilter);

  const fmtWhen = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
        <p className="text-red-700">{error}</p>
        <button onClick={loadRequests} className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-sm">
          <RefreshCw className="h-4 w-4" /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-3 py-1 rounded-full text-sm font-medium ${statusFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          All ({requests.length})
        </button>
        {Object.entries(UA_REQ_STATUSES).map(([key, cfg]) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key)}
            className={`px-3 py-1 rounded-full text-sm font-medium ${statusFilter === key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {cfg.label} ({counts[key] || 0})
          </button>
        ))}
        <button onClick={loadRequests} className="ml-auto flex items-center gap-1.5 px-3 py-1 text-sm text-gray-600 hover:text-gray-900">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {shown.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-10 text-center text-gray-500">
          <Inbox className="h-10 w-10 mx-auto mb-3 text-gray-400" />
          No {statusFilter === 'all' ? '' : `${UA_REQ_STATUSES[statusFilter]?.label.toLowerCase()} `}requests yet.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-gray-600">
                <th className="px-3 py-2.5 font-semibold">Received</th>
                <th className="px-3 py-2.5 font-semibold">Housing Authority</th>
                <th className="px-3 py-2.5 font-semibold">Contact</th>
                <th className="px-3 py-2.5 font-semibold">Needs schedule for</th>
                <th className="px-3 py-2.5 font-semibold">Journey</th>
                <th className="px-3 py-2.5 font-semibold">Payment</th>
                <th className="px-3 py-2.5 font-semibold">Files</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <React.Fragment key={r.id}>
                  <tr
                    className="border-b border-gray-100 hover:bg-blue-50/40 cursor-pointer"
                    onClick={() => toggleExpand(r.id)}
                  >
                    <td className="px-3 py-2.5 whitespace-nowrap text-gray-600">{fmtWhen(r.created_at)}</td>
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-gray-900">{r.housing_authority}</div>
                      <div className="text-xs text-gray-500">
                        {r.city_state}{r.pha_code ? ` · ${r.pha_code}` : ''}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="text-gray-900">{r.contact_name}{r.title ? <span className="text-gray-500"> · {r.title}</span> : null}</div>
                      <div className="text-xs text-gray-500">
                        <a href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:text-blue-800">{r.email}</a>
                        {r.phone ? ` · ${r.phone}` : ''}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap font-medium text-gray-900">
                      {r.next_schedule_month || <span className="text-gray-400 font-normal">—</span>}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {(() => {
                        const stage = UA_PAYMENT_STAGES[r.payment_stage] || UA_PAYMENT_STAGES.form_submitted;
                        return (
                          <span
                            title={stage.hint + (r.paid_at ? ` · ${fmtWhen(r.paid_at)}` : '')}
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${stage.badge}`}
                          >
                            {stage.label}{r.payment_stage === 'paid' ? ' ✓' : ''}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-600 max-w-[160px]">
                      {r.payment_method
                        ? <span title={r.payment_method}>Requested: {r.payment_method.length > 40 ? `${r.payment_method.slice(0, 40)}…` : r.payment_method}</span>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      {Array.isArray(r.files) && r.files.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {r.files.map((f, i) => (
                            <a
                              key={f.id}
                              href={fileUrl(r.id, f.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs"
                              title={f.filename || `Download file ${i + 1}`}
                            >
                              <Download className="h-3.5 w-3.5 shrink-0" />
                              <span className="truncate max-w-[180px]">{f.filename || `file ${i + 1}`}</span>
                            </a>
                          ))}
                        </div>
                      ) : r.has_schedule_file ? (
                        <a
                          href={fileUrl(r.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs"
                          title={r.current_schedule_filename || 'Download uploaded schedule'}
                        >
                          <Download className="h-3.5 w-3.5" /> schedule
                        </a>
                      ) : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={r.status}
                        disabled={savingId === r.id}
                        onChange={(e) => changeStatus(r.id, e.target.value)}
                        className={`text-xs font-medium rounded-full px-2.5 py-1 border-0 cursor-pointer ${UA_REQ_STATUSES[r.status]?.badge || 'bg-gray-100 text-gray-700'}`}
                      >
                        {Object.entries(UA_REQ_STATUSES).map(([key, cfg]) => (
                          <option key={key} value={key}>{cfg.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  {expanded.has(r.id) && (
                    <tr className="bg-blue-50/30 border-b border-blue-100">
                      <td colSpan={8} className="px-6 py-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-gray-700">
                          <div>
                            <span className="text-gray-500 block">Households (all programs)</span>
                            {r.households_across_programs || '—'}
                          </div>
                          <div>
                            <span className="text-gray-500 block">S8A member</span>
                            {r.s8a_member === true ? 'Yes' : r.s8a_member === false ? 'No' : '—'}
                          </div>
                          <div>
                            <span className="text-gray-500 block">Chatbot / translation customer</span>
                            {r.has_chatbot_translation === true ? 'Yes' : r.has_chatbot_translation === false ? 'No' : '—'}
                          </div>
                          <div>
                            <span className="text-gray-500 block">Status changed</span>
                            {fmtWhen(r.status_updated_at)}
                          </div>
                          {r.paid_at && (
                            <div>
                              <span className="text-gray-500 block">Paid via Stripe</span>
                              {fmtWhen(r.paid_at)}
                            </div>
                          )}
                          {r.payment_method && (
                            <div className="col-span-2">
                              <span className="text-gray-500 block">Payment request ({fmtWhen(r.payment_requested_at)})</span>
                              {r.payment_method}
                            </div>
                          )}
                          {r.notes && (
                            <div className="col-span-2 md:col-span-4">
                              <span className="text-gray-500 block">Notes</span>
                              <span className="whitespace-pre-wrap">{r.notes}</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-400 flex items-center gap-1.5">
        <FileText className="h-3.5 w-3.5" />
        Click a row for details. Requests come from gethousingai.com/utility-allowances/join; statuses save immediately.
      </p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Main Dashboard
// ═══════════════════════════════════════════════════════════
const DashboardPage = () => {
  const [currentUser, setCurrentUser] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [activeTab, setActiveTab] = useState('analytics');
  const [acopAccess, setAcopAccess] = useState({ enabled: false, hasAudit: false, orgName: null, loaded: false });
  const navigate = useNavigate();

  const isWidgetBoss = currentUser === 'widgetboss';

  // UA tab shows iff the org has a current schedule in the compliance DB
  // (200 = yes, 404 = no) — same source the tab and PDF render from.
  const [utilityEnabled, setUtilityEnabled] = useState(false);
  useEffect(() => {
    const slug = (currentUser || '').toLowerCase();
    if (!slug) { setUtilityEnabled(false); return; }
    let cancelled = false;
    fetch(`${COMPLIANCE_API_URL}/api/utility-allowance/${encodeURIComponent(slug)}`)
      .then((res) => { if (!cancelled) setUtilityEnabled(res.ok); })
      .catch(() => { if (!cancelled) setUtilityEnabled(false); });
    return () => { cancelled = true; };
  }, [currentUser]);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    const username = localStorage.getItem('currentUser');

    if (!isAuthenticated || !username) {
      navigate('/login');
      return;
    }

    setCurrentUser(username);
    // Housing Navigator MA has its own dashboard (HousingNavigatorAnalytics).
    if (username === 'housingnavigator') setLoading(false);
    else {
      loadAnalytics(username);
      loadAcopAccess(username);
    }

    // Idle timeout (30 min)
    let idleTimer, warningTimer;
    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      clearTimeout(warningTimer);
      warningTimer = setTimeout(() => {
        const stay = window.confirm('You will be logged out in 5 minutes due to inactivity. Click OK to stay logged in.');
        if (!stay) handleLogout();
      }, 25 * 60 * 1000);
      idleTimer = setTimeout(() => { alert('Logged out due to inactivity.'); handleLogout(); }, 30 * 60 * 1000);
    };
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, true));
    resetIdleTimer();
    return () => {
      clearTimeout(idleTimer);
      clearTimeout(warningTimer);
      events.forEach(e => document.removeEventListener(e, resetIdleTimer, true));
    };
  }, [navigate]);

  const loadAnalytics = async (username, days = 30) => {
    setLoading(true);
    setError('');
    try {
      const endpoint = username === 'widgetboss'
        ? `${API_URL}/analytics?days=${days}`
        : `${API_URL}/analytics/${username}?days=${days}`;

      const response = await fetch(endpoint);
      if (!response.ok) throw new Error(`Failed to load analytics: ${response.status}`);
      const data = await response.json();

      if (data.conversationTimestamps) {
        data.conversationsPerDay = groupConversationsByLocalDate(data.conversationTimestamps);
      }
      setAnalytics(data);
    } catch (err) {
      console.error('❌ Analytics error:', err);
      setError('Failed to load analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadAcopAccess = async (username) => {
    try {
      const res = await fetch(`${COMPLIANCE_API_URL}/api/acop-monitor/access/${username}`);
      if (!res.ok) {
        setAcopAccess({ enabled: false, hasAudit: false, orgName: null, loaded: true });
        return;
      }
      const data = await res.json();
      setAcopAccess({
        enabled: data.enabled === true,
        hasAudit: data.hasAudit === true,
        orgName: data.orgName || null,
        loaded: true,
      });
    } catch (err) {
      console.error('ACOP access check failed:', err);
      setAcopAccess({ enabled: false, hasAudit: false, orgName: null, loaded: true });
    }
  };

  const handlePeriodChange = (days) => {
    setSelectedPeriod(days);
    loadAnalytics(currentUser, days);
  };

  const handleRefresh = () => loadAnalytics(currentUser, selectedPeriod);

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('loginTime');
    navigate('/login');
  };

  const formatNumber = (num) => num >= 1000 ? (num / 1000).toFixed(1) + 'k' : num?.toString() || '0';

  // ── Sub-components ──
  const StatCard = ({ icon: Icon, title, value, subtitle, color = 'blue' }) => (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
      <p className="text-sm text-gray-600 mb-1">{title}</p>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    </div>
  );

  const PieChart = ({ languageData }) => {
    if (!languageData?.length) return <p className="text-gray-500 text-center">No language data available</p>;
    const total = languageData.reduce((sum, item) => sum + parseInt(item.count), 0);
    if (total === 0) return <p className="text-gray-500 text-center">No language data available</p>;
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];
    let cumulativePercentage = 0;
    const segments = languageData.map((item, index) => {
      const percentage = (parseInt(item.count) / total) * 100;
      const startAngle = cumulativePercentage * 3.6;
      const endAngle = (cumulativePercentage + percentage) * 3.6;
      cumulativePercentage += percentage;
      const largeArcFlag = percentage > 50 ? 1 : 0;
      const radius = 120, centerX = 150, centerY = 150;
      const x1 = centerX + radius * Math.cos((startAngle - 90) * Math.PI / 180);
      const y1 = centerY + radius * Math.sin((startAngle - 90) * Math.PI / 180);
      const x2 = centerX + radius * Math.cos((endAngle - 90) * Math.PI / 180);
      const y2 = centerY + radius * Math.sin((endAngle - 90) * Math.PI / 180);
      return {
        ...item, percentage: percentage.toFixed(1), color: colors[index % colors.length],
        pathData: `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`
      };
    });
    return (
      <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
        <svg width="300" height="300" className="transform -rotate-90 flex-shrink-0">
          {segments.map((s, i) => <path key={i} d={s.pathData} fill={s.color} stroke="white" strokeWidth="3" />)}
        </svg>
        <div className="space-y-3 min-w-0">
          {segments.map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }}></div>
              <span className="text-sm font-medium text-gray-700 capitalize truncate">{getLanguageName(s.language)}</span>
              <span className="text-sm text-gray-600 flex-shrink-0">{s.percentage}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const WordCloud = ({ keywords }) => {
    if (!keywords?.length) return <p className="text-gray-500 text-center">No keyword data available</p>;
    const maxFreq = Math.max(...keywords.map(k => k.frequency));
    const minFreq = Math.min(...keywords.map(k => k.frequency));
    const range = maxFreq - minFreq || 1;
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#84CC16'];
    return (
      <div className="flex flex-wrap gap-3 items-center justify-center p-4">
        {keywords.slice(0, 20).map((item, index) => (
          <span key={index} className="inline-block font-medium cursor-pointer hover:opacity-80 transition-opacity relative group"
            style={{ fontSize: `${14 + ((item.frequency - minFreq) / range) * 18}px`, color: colors[index % colors.length] }}>
            {item.keyword}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              {item.frequency} mentions
            </div>
          </span>
        ))}
      </div>
    );
  };

  const BarChartComponent = ({ conversationsData }) => {
    if (!conversationsData?.length) return <p className="text-gray-500 text-center">No conversation data available</p>;
    const displayData = conversationsData.slice(-selectedPeriod);
    const maxConversations = Math.max(...displayData.map(d => d.conversations));
    if (maxConversations === 0) return <p className="text-gray-500 text-center">No conversation data available</p>;
    const today = new Date().toLocaleDateString('en-CA');
    return (
      <div className="space-y-4">
        <div className="relative h-64 p-4 rounded-lg bg-white">
          <div className="flex items-end justify-center gap-1 h-full">
            {displayData.map((day, index) => {
              const ratio = day.conversations / maxConversations;
              const heightPercent = Math.max(ratio * 100, 5);
              const [year, month, dateNum] = day.date.split('-').map(Number);
              const dayDate = new Date(year, month - 1, dateNum);
              const isToday = day.date === today;
              return (
                <div key={index} className="relative flex-1 h-full flex flex-col justify-end items-center max-w-8">
                  <div className="relative w-full h-full flex flex-col justify-end group">
                    <div className={`w-full rounded-t transition-all duration-300 hover:opacity-80 ${isToday ? 'bg-blue-600' : 'bg-blue-400'}`}
                      style={{ height: `${heightPercent}%`, minHeight: '4px' }} />
                    <div className="absolute left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800/80 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10"
                      style={{ bottom: `calc(${heightPercent}% + 24px)` }}>
                      {dayDate.toLocaleDateString()}<br />{day.conversations} conversations
                    </div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 text-xs font-medium text-gray-900"
                      style={{ bottom: `calc(${heightPercent}% + 4px)` }}>{day.conversations}</div>
                  </div>
                  <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-600 rotate-90 origin-center whitespace-nowrap">
                    {`${month}/${dateNum}`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="text-center text-sm text-gray-500 mt-12">Daily conversations over the last {selectedPeriod} days</div>
      </div>
    );
  };

  if (currentUser === 'housingnavigator') {
    return <HousingNavigatorAnalytics apiUrl={API_URL} />;
  }

  // ── Loading state ──
  if (loading && activeTab === 'analytics') {
    return (
      <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition} className="min-h-screen pt-20 bg-gray-50">
        <section className="py-20">
          <div className="container mx-auto px-4 flex justify-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
          </div>
        </section>
      </motion.div>
    );
  }

  return (
    <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition} className="min-h-screen pt-20 bg-gray-50">
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">

            {/* Header */}
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <BarChart3 className="h-16 w-16 text-blue-600" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {isWidgetBoss ? 'Widget Analytics Dashboard' : 'Housing Authority Dashboard'}
              </h1>
              <p className="text-xl text-gray-600">
                {isWidgetBoss ? 'Aggregate analytics across all housing authorities' : 'All your HA chatbot analytics in one place'}
              </p>
            </motion.div>

            {/* User Info & Controls */}
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }} className="bg-white rounded-xl p-6 shadow-lg mb-8">
              <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <User className="h-6 w-6 text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-900">
                    Welcome back, {isWidgetBoss ? 'Widget Boss' : currentUser}!
                  </h2>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  {/* Tab Switcher: Analytics for everyone, Conversations for widgetboss, ACOP Monitor for all (greyed out if not enabled) */}
                  <div className="flex flex-wrap gap-1 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setActiveTab('analytics')}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${activeTab === 'analytics' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                      Analytics
                    </button>
                    {isWidgetBoss && (
                      <button
                        onClick={() => setActiveTab('conversations')}
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${activeTab === 'conversations' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                      >
                        Conversations
                      </button>
                    )}
                    {isWidgetBoss && (
                      <button
                        onClick={() => setActiveTab('uarequests')}
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === 'uarequests' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                      >
                        <Inbox className="h-3.5 w-3.5" />
                        UA Requests
                      </button>
                    )}
                    {isWidgetBoss && (
                      <button
                        onClick={() => setActiveTab('uatracker')}
                        title="Utility allowance delivery funnel per authority"
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === 'uatracker' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                      >
                        <ClipboardCheck className="h-3.5 w-3.5" />
                        UA Tracker
                      </button>
                    )}
                    {isWidgetBoss && (
                      <button
                        onClick={() => setActiveTab('qa')}
                        title="Chatbot test-suite results, per org"
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === 'qa' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                      >
                        <ClipboardCheck className="h-3.5 w-3.5" />
                        QA Results
                      </button>
                    )}
                    {isWidgetBoss && (
                      <button
                        onClick={() => setActiveTab('outreach')}
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === 'outreach' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                      >
                        <Target className="h-3.5 w-3.5" />
                        Outreach
                      </button>
                    )}
                    {isWidgetBoss && (
                      <button
                        onClick={() => setActiveTab('lihtc')}
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === 'lihtc' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                      >
                        <Building2 className="h-3.5 w-3.5" />
                        LIHTC
                      </button>
                    )}
                    {isWidgetBoss && (
                      <button
                        onClick={() => setActiveTab('billing')}
                        title="What each customer has been billed, every product"
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === 'billing' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                      >
                        <Receipt className="h-3.5 w-3.5" />
                        Billing
                      </button>
                    )}
                    <button
                      onClick={() => acopAccess.enabled && setActiveTab('acop')}
                      disabled={!acopAccess.enabled}
                      title={acopAccess.enabled
                        ? 'View ACOP compliance audit'
                        : 'Available with the ACOP Monitor add-on. Contact HousingAI to enable.'}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${
                        activeTab === 'acop'
                          ? 'bg-white text-blue-700 shadow-sm'
                          : acopAccess.enabled
                            ? 'text-gray-600 hover:text-gray-900'
                            : 'text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {!acopAccess.enabled && <Lock className="h-3 w-3" />}
                      {acopAccess.enabled && <ClipboardCheck className="h-3.5 w-3.5" />}
                      ACOP Monitor
                    </button>
                    <button
                      onClick={() => utilityEnabled && setActiveTab('utility')}
                      disabled={!utilityEnabled}
                      title={utilityEnabled
                        ? 'View utility allowance schedule'
                        : 'No utility allowance on file for this organization.'}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${
                        activeTab === 'utility'
                          ? 'bg-white text-blue-700 shadow-sm'
                          : utilityEnabled
                            ? 'text-gray-600 hover:text-gray-900'
                            : 'text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {!utilityEnabled && <Lock className="h-3 w-3" />}
                      {utilityEnabled && <Zap className="h-3.5 w-3.5" />}
                      Utility Allowance
                    </button>
                  </div>

                  {/* Period Selector (only on analytics tab) */}
                  {activeTab === 'analytics' && (
                    <div className="flex gap-2">
                      {[7, 30, 90].map(days => (
                        <button key={days} onClick={() => handlePeriodChange(days)}
                          className={`px-3 py-1 rounded ${selectedPeriod === days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                          {days}d
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Refresh (only on analytics tab) */}
                  {activeTab === 'analytics' && (
                    <button onClick={handleRefresh} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                      <RefreshCw className="h-4 w-4" /> Refresh
                    </button>
                  )}
                </div>
              </div>
            </motion.div>

            {error && activeTab === 'analytics' && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-8">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {/* ── Conversations Tab ── */}
            {activeTab === 'conversations' && isWidgetBoss && (
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}
                className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">All Conversations</h3>
                <ConversationsTab />
              </motion.div>
            )}

            {/* ── UA Requests Tab (widgetboss only) ── */}
            {activeTab === 'uarequests' && isWidgetBoss && (
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}
                className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Utility Allowance Requests</h3>
                <UARequestsTab />
              </motion.div>
            )}

            {activeTab === 'uatracker' && isWidgetBoss && (
              <UATrackerTab />
            )}

            {/* ── QA Results Tab (widgetboss only) ── */}
            {activeTab === 'qa' && isWidgetBoss && (
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
                <QAResultsPage embedded />
              </motion.div>
            )}

            {/* ── Outreach Tab (widgetboss only) ── */}
            {activeTab === 'outreach' && isWidgetBoss && (
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}
                className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-baseline justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">PHA Outreach</h3>
                  <span className="text-xs text-gray-400">Every public housing authority in the country, and where each one stands.</span>
                </div>
                <OutreachTab currentUser={currentUser} />
              </motion.div>
            )}

            {/* ── LIHTC Tab (widgetboss only) ── */}
            {activeTab === 'lihtc' && isWidgetBoss && (
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}
                className="bg-white rounded-xl p-6 shadow-lg">
                <LihtcTab />
              </motion.div>
            )}

            {/* ── Billing Tab (widgetboss only) ── */}
            {activeTab === 'billing' && isWidgetBoss && (
              <BillingTab />
            )}

            {/* ── ACOP Monitor Tab ── */}
            {activeTab === 'acop' && acopAccess.enabled && (
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}>
                <AcopMonitor currentUser={currentUser} />
              </motion.div>
            )}

            {/* ── Utility Allowance Tab ── */}
            {activeTab === 'utility' && utilityEnabled && (
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}>
                <UtilityAllowance currentUser={currentUser} />
              </motion.div>
            )}

            {/* ── Analytics Tab ── */}
            {activeTab === 'analytics' && analytics && (
              <>
                {/* Widget Boss: Organization Usage */}
                {isWidgetBoss && analytics.organizationUsage && (
                  <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}
                    className="bg-white rounded-xl p-6 shadow-lg mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Organization Usage</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2">Organization</th>
                            <th className="text-left py-2">Conversations</th>
                            <th className="text-left py-2">Avg Length</th>
                            <th className="text-left py-2">Active Days</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.organizationUsage.map((org, index) => (
                            <tr key={index} className="border-b">
                              <td className="py-2 font-medium capitalize">{org.org}</td>
                              <td className="py-2">{org.total_conversations}</td>
                              <td className="py-2">{parseFloat(org.avg_conversation_length || 0).toFixed(1)}</td>
                              <td className="py-2">{org.active_days}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </motion.div>
                )}

                {/* Stats Cards */}
                <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <StatCard icon={MessagesSquare} title="Total Conversations" value={formatNumber(analytics.totalConversations)} subtitle={`Last ${selectedPeriod} days`} color="blue" />
                  <StatCard icon={MessageSquareText} title="Avg Conversation Length" value={analytics.averageConversationLength || '0'} subtitle="Messages per conversation" color="green" />
                  <StatCard icon={Globe} title="Languages Used" value={analytics.languageDistribution?.length || 0} subtitle="Different languages" color="purple" />
                  <StatCard icon={Tag} title="Popular Topics" value={analytics.topKeywords?.length || 0} subtitle="Unique keywords" color="orange" />
                </motion.div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}
                    className="bg-white rounded-xl p-6 shadow-lg">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Language Distribution</h3>
                    <PieChart languageData={analytics.languageDistribution} />
                  </motion.div>
                  <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.6 }}
                    className="bg-white rounded-xl p-6 shadow-lg">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Most Frequent Topics</h3>
                    <WordCloud keywords={analytics.topKeywords} />
                  </motion.div>
                </div>

                {/* Daily Conversations Chart */}
                <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.7 }}
                  className="bg-white rounded-xl p-6 shadow-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Daily Conversations</h3>
                  <BarChartComponent conversationsData={analytics.conversationsPerDay} />
                </motion.div>
              </>
            )}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default DashboardPage;